import { useState, useEffect } from 'react';
import { Button, Label } from '@fyos/components';
import { X, Skull } from 'lucide-react';

interface KillReasonModalProps {
  open: boolean;
  itemTitle: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export default function KillReasonModal({ open, itemTitle, onConfirm, onCancel }: KillReasonModalProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
      setError('');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [open]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onCancel();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onCancel]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError('A reason is required when killing an item');
      return;
    }
    onConfirm(reason.trim());
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-gray-400/50 dark:bg-black/60 backdrop-blur-sm"
        onClick={onCancel}
        aria-hidden="true"
      />
      <div className="relative z-[60] w-full max-w-md" role="dialog" aria-modal="true">
        <div className="relative rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-800">
          <button
            type="button"
            onClick={onCancel}
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-white"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-950/50">
              <Skull className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Kill Item</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[280px]">{itemTitle}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-3">
              <div>
                <Label htmlFor="kill-reason" required>
                  Why is this item being killed?
                </Label>
                <textarea
                  id="kill-reason"
                  value={reason}
                  onChange={(e) => { setReason(e.target.value); setError(''); }}
                  placeholder="e.g. No longer relevant, duplicate of another item, out of scope..."
                  rows={3}
                  autoFocus
                  className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-500"
                />
              </div>
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-2.5 dark:border-red-800 dark:bg-red-950/30">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </div>

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit" variant="danger">
                Kill Item
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
